/**
 * Groq API Integration Service
 * Free cloud LLM for heavy AI tasks
 * https://console.groq.com
 */

// Note: In production, this would use fetch to call your backend API
// which calls Groq to avoid exposing API keys in frontend code

export const groqService = {
  apiKey: null,
  baseUrl: 'https://api.groq.com/openai/v1',
  model: 'mixtral-8x7b-32768', // Free model - check Groq for latest

  /**
   * Set API key (should come from secure backend, not frontend)
   */
  setApiKey(key) {
    this.apiKey = key
  },

  /**
   * Ping Groq service to check availability
   */
  async ping() {
    try {
      const response = await fetch('https://api.groq.com/openai/v1/models', {
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
        },
      })
      return response.ok
    } catch (error) {
      console.error('Groq ping failed:', error)
      throw error
    }
  },

  /**
   * Analyze data using Groq
   */
  async analyzeData(data, context = '') {
    try {
      const prompt = `Analyze the following data and provide detailed insights:

Context: ${context}
Data: ${JSON.stringify(data, null, 2)}

Provide:
1. Key findings
2. Anomalies detected
3. Recommendations
4. Risk assessment`

      return await this._callGroq(prompt)
    } catch (error) {
      console.error('Groq analysis failed:', error)
      throw error
    }
  },

  /**
   * Generate insights from metrics
   */
  async generateInsights(metrics, category = 'general') {
    try {
      const prompt = `Generate ${category} business insights from these metrics:
${JSON.stringify(metrics, null, 2)}

Format as JSON array with objects containing: {type, text, priority}`

      const response = await this._callGroq(prompt)

      // Parse and structure response
      try {
        return JSON.parse(response)
      } catch (e) {
        return [{ type: 'neutral', text: response, priority: 'low' }]
      }
    } catch (error) {
      console.error('Groq insights generation failed:', error)
      throw error
    }
  },

  /**
   * Generate strategic recommendations
   */
  async generateRecommendations(situation, context = {}) {
    try {
      const prompt = `Based on this situation, generate strategic recommendations:

Situation: ${situation}
Context: ${JSON.stringify(context, null, 2)}

For each recommendation provide: action, rationale, impact, timeline.
Format as JSON array.`

      const response = await this._callGroq(prompt)

      try {
        return JSON.parse(response)
      } catch (e) {
        return [
          {
            action: 'Review and analyze',
            rationale: response,
            impact: 'medium',
            timeline: 'Immediate',
          },
        ]
      }
    } catch (error) {
      console.error('Groq recommendations failed:', error)
      throw error
    }
  },

  /**
   * Process security/business alerts
   */
  async processAlert(alertData, severity = 'medium') {
    try {
      const prompt = `Process this ${severity} severity alert:
${JSON.stringify(alertData, null, 2)}

Determine appropriate action. Response format:
{
  "action": "alert|investigate|log",
  "priority": "critical|high|medium|low",
  "reason": "explanation",
  "recommendations": ["list", "of", "actions"]
}`

      const response = await this._callGroq(prompt)

      try {
        return JSON.parse(response)
      } catch (e) {
        return {
          action: 'log',
          priority: severity,
          reason: response,
          recommendations: [],
        }
      }
    } catch (error) {
      console.error('Groq alert processing failed:', error)
      throw error
    }
  },

  /**
   * Fallback voice response if Ollama unavailable
   */
  async generateVoiceResponse(userInput, context = {}) {
    try {
      const prompt = `Respond naturally and conversationally to: "${userInput}"
Context: ${JSON.stringify(context)}

Keep response concise and personable.`

      return await this._callGroq(prompt)
    } catch (error) {
      console.error('Groq voice response failed:', error)
      throw error
    }
  },

  /**
   * Adaptive response using Groq
   */
  async generateAdaptiveResponse(userMessage, userPattern = {}) {
    try {
      const prompt = `Generate a response that adapts to user pattern:
Message: "${userMessage}"
User Pattern: ${JSON.stringify(userPattern)}

Adapt tone, style, and detail level to match user preferences.`

      return await this._callGroq(prompt)
    } catch (error) {
      console.error('Groq adaptive response failed:', error)
      throw error
    }
  },

  /**
   * Internal method to call Groq API
   * In production, route through your backend to keep API key secure
   */
  async _callGroq(prompt, temperature = 0.7, maxTokens = 1024) {
    // NOTE: In production, use a backend endpoint instead of calling directly
    // Example backend endpoint call:
    // POST /api/ai/groq
    // { prompt, temperature, maxTokens }

    // For development/demo, you would implement this as:
    // return await fetch('/api/ai/groq', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ prompt, temperature, maxTokens })
    // }).then(r => r.json())

    // Placeholder for demo - replace with actual API call
    if (!this.apiKey) {
      throw new Error('Groq API key not configured')
    }

    try {
      // This is where the actual Groq API call would go
      // For now, returning mock response
      console.log('[Groq] Processing:', prompt.substring(0, 50) + '...')

      return 'Mock Groq response - configure API key and backend endpoint'
    } catch (error) {
      console.error('Groq API call failed:', error)
      throw error
    }
  },
}

export default groqService
